import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatCard, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Users, UserCheck, Wallet, CalendarClock, ArrowRight } from "lucide-react";
import { formatINR, monthName } from "@/utils/format";

export default function AdminDashboard() {
  const [employees, setEmployees] = useState(null);
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/employees"), api.get("/payroll/runs")])
      .then(([e, r]) => { setEmployees(e.data); setRuns(r.data); })
      .catch(() => setEmployees([]));
  }, []);

  if (employees === null) return <Loading />;

  const active = employees.filter((e) => e.status === "active").length;
  const monthlyNet = employees.filter((e) => e.status === "active")
    .reduce((s, e) => s + (e.monthly_amount || 0), 0);
  const lastRun = runs[0];

  return (
    <div>
      <PageHeader title="Dashboard" description="A quick pulse of your organisation and payroll." testid="admin-dashboard" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Employees" value={employees.length} hint={`${active} active`} icon={Users} testid="stat-employees" />
        <StatCard label="Active Payroll" value={active} hint="on this month's run" icon={UserCheck} testid="stat-active" />
        <StatCard label="Monthly Outflow" value={formatINR(monthlyNet)} hint="net, active staff" icon={Wallet} accent testid="stat-outflow" />
        <StatCard label="Runs Generated" value={runs.length} hint={lastRun ? `latest ${monthName(lastRun.month)} ${lastRun.year}` : "none yet"} icon={CalendarClock} testid="stat-runs" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-md">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h2 className="font-display font-semibold">Recent payroll runs</h2>
            <Button asChild variant="ghost" size="sm" className="gap-1.5">
              <Link to="/admin/payroll" data-testid="goto-payroll">Open payroll <ArrowRight className="w-3.5 h-3.5" /></Link>
            </Button>
          </div>
          {runs.length === 0 ? (
            <div className="px-5 py-10 text-sm text-muted-foreground text-center">
              No payroll generated yet. Head to Payroll Runs to create the first month.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {runs.slice(0, 6).map((r) => (
                <div key={`${r.year}-${r.month}`} className="px-5 py-3.5 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{monthName(r.month)} {r.year}</div>
                    <div className="text-xs text-muted-foreground">FY {r.fy} · {r.count} payslips</div>
                  </div>
                  <div className="text-right tabular">
                    <div className="font-medium text-sm">{formatINR(r.total_net)}</div>
                    <div className="text-xs text-muted-foreground">net disbursed</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-md p-5">
          <h2 className="font-display font-semibold mb-4">Quick actions</h2>
          <div className="space-y-2.5">
            <QuickLink to="/admin/payroll" label="Run this month's payroll" />
            <QuickLink to="/admin/employees" label="Add or edit an employee" />
            <QuickLink to="/admin/holidays" label="Manage holiday calendar" />
            <QuickLink to="/admin/settings" label="Company & payslip settings" />
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickLink({ to, label }) {
  return (
    <Link to={to} className="flex items-center justify-between px-3.5 py-3 rounded-md border border-border hover:border-foreground/30 hover:-translate-y-[1px] transition-transform text-sm group">
      {label}
      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-[#FF4500]" />
    </Link>
  );
}
